import nacl from "tweetnacl";
import { encodeBase64, decodeBase64, encodeUTF8, decodeUTF8 } from "tweetnacl-util";
import { scryptAsync } from "@noble/hashes/scrypt.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { randomBytes as nobleRandomBytes, bytesToHex, concatBytes } from "@noble/hashes/utils.js";
import { generateMnemonic as bip39GenerateMnemonic, validateMnemonic as bip39ValidateMnemonic } from "@scure/bip39";
import { wordlist } from "@scure/bip39/wordlists/english.js";

// Revision 5 (Decisions 4, 5, 11, 12): `chatKey` is random again, NOT
// derived from the password. This module now additionally provides:
//   - random `chatKey`/salt/PIN generation (CSPRNG, via @noble/hashes'
//     `randomBytes` — pure JS/TS, already a dependency for scrypt, portable
//     across browser/Node/Hermes without relying on `crypto.getRandomValues`
//     or tweetnacl's own PRNG setup being pre-configured on every runtime);
//   - `wrapChatKey`/`unwrapChatKey` (nacl.secretbox, keyed by a "wrap key");
//   - four additional scrypt derivations beyond `deriveAuthSecret`
//     (`derivePasswordWrapKey`, `deriveRecoveryAuthProof`,
//     `deriveRecoveryWrapKey`, `derivePinWrapKey`);
//   - BIP-39 mnemonic generation/validation via `@scure/bip39` (Decision 11).
//
// IMPORTANT: the SCRYPT_PARAMS and every derivation's salt-suffix string
// below must stay byte-for-byte identical across `web/api/lib/auth.ts`,
// `mobile/src/lib/crypto.ts`, and `mcp/src/lib.ts` — a mismatch would
// silently derive different keys per client and break decryption/unwrap
// across devices.
const SCRYPT_PARAMS = { N: 2 ** 15, r: 8, p: 1, dkLen: 32 } as const;

async function scryptDerive(secret: string, saltWithSuffix: string): Promise<string> {
  const out = await scryptAsync(secret, saltWithSuffix, SCRYPT_PARAMS);
  return encodeBase64(out);
}

/** `authSecret` — sent to the server to authenticate. Never used for encryption. */
export function deriveAuthSecret(password: string, salt: string): Promise<string> {
  return scryptDerive(password, `${salt}:auth`);
}

/** `passwordWrapKey` — unwraps `wrapped_chat_key_password`. Never sent anywhere. */
export function derivePasswordWrapKey(password: string, salt: string): Promise<string> {
  return scryptDerive(password, `${salt}:wrap`);
}

/**
 * Combined authSecret + passwordWrapKey derivation. Both values used to come
 * from two INDEPENDENT scrypt calls (`deriveAuthSecret` +
 * `derivePasswordWrapKey` above) — scrypt is the expensive part, JS here is
 * single-threaded, so running it twice per login roughly doubled wall-clock
 * time (~10-12s on a mid-range phone) for no extra security benefit, since
 * both calls already share the same password+salt and only differ by a
 * suffix. This runs scrypt ONCE (with a doubled `dkLen` to get enough output
 * for two keys) and splits the two domain-separated subkeys out of that
 * single expensive result via a cheap SHA-256 label mix — still fully
 * dependent on the one scrypt stretch (an attacker still has to pay the full
 * scrypt cost to test each password guess), so no security is lost, but the
 * expensive part only runs once.
 *
 * `deriveAuthSecret`/`derivePasswordWrapKey` above are kept, UNCHANGED, only
 * for the one-time account migration (`server/src/lib/migrate-to-login-v2.ts`
 * + the account owner running the paired client-side rekey step once) that
 * moves an existing account from the old two-scrypt scheme to this one.
 * Every login/register/recovery-reset call site should use this instead.
 */
export interface LoginKeys {
  authSecret: string;
  passwordWrapKey: string;
}

const LOGIN_KEYS_SCRYPT_PARAMS = { N: 2 ** 15, r: 8, p: 1, dkLen: 64 } as const;

export async function deriveLoginKeys(password: string, salt: string): Promise<LoginKeys> {
  const stretched = await scryptAsync(password, `${salt}:v2`, LOGIN_KEYS_SCRYPT_PARAMS);
  return {
    authSecret: encodeBase64(sha256(concatBytes(stretched, decodeUTF8("auth")))),
    passwordWrapKey: encodeBase64(sha256(concatBytes(stretched, decodeUTF8("wrap"))))
  };
}

/** `recoveryAuthProof` — sent to `recovery-confirm` to prove the mnemonic. Never used for encryption. */
export function deriveRecoveryAuthProof(mnemonic: string, salt: string): Promise<string> {
  return scryptDerive(normalizeMnemonic(mnemonic), `${salt}:recovery-verify`);
}

/** `recoveryWrapKey` — unwraps `wrapped_chat_key_recovery`. Never sent anywhere. */
export function deriveRecoveryWrapKey(mnemonic: string, salt: string): Promise<string> {
  return scryptDerive(normalizeMnemonic(mnemonic), `${salt}:recovery-wrap`);
}

/** `pinWrapKey` — unwraps `wrappedChatKeyForPin`. Never sent anywhere (only its hash is). */
export function derivePinWrapKey(pin: string, linkSalt: string): Promise<string> {
  return scryptDerive(pin, linkSalt);
}

/**
 * `deviceAuthWrapKey` — used by the `/authorize` screen to wrap `chatKey` for
 * the `mcp` device-authorization ("device code") login flow, mirroring
 * `derivePinWrapKey`'s KDF call shape. Deliberately keyed off a fixed
 * domain-separation suffix rather than a second, independently-random salt
 * like `linkSalt`: unlike a 6-digit PIN, the `userCode` here is already an
 * ~40-bit-entropy, single-use, short-TTL secret minted by the server, and
 * there is no round-trip through which this (never-authenticated-until-now)
 * browser tab could otherwise learn a server-chosen salt without adding one.
 * See `server/src/routes/auth/device-authorize/*.ts` for the full flow.
 */
export function deriveDeviceAuthWrapKey(userCode: string): Promise<string> {
  return scryptDerive(normalizeUserCode(userCode), "bridge-device-authorize:wrap");
}

/** Canonicalizes a user-typed device-authorization code (strip dashes/spaces, uppercase) before deriving/comparing. */
export function normalizeUserCode(code: string): string {
  return code.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
}

/** Case/whitespace-normalizes a user-entered mnemonic before deriving/validating it. */
export function normalizeMnemonic(mnemonic: string): string {
  return mnemonic.trim().toLowerCase().replace(/\s+/g, " ");
}

/** Generates a fresh 12-word English BIP-39 mnemonic (128 bits of entropy) — Decision 11. */
export function generateMnemonic(): string {
  return bip39GenerateMnemonic(wordlist, 128);
}

/** Validates a mnemonic's word count/checksum against the English wordlist (client-side UX only). */
export function isValidMnemonic(mnemonic: string): boolean {
  return bip39ValidateMnemonic(normalizeMnemonic(mnemonic), wordlist);
}

/** Generates a fresh random `chatKey` (32 bytes, CSPRNG) — Decision 4. Never derived from anything. */
export function generateChatKey(): string {
  return encodeBase64(nobleRandomBytes(32));
}

/** Generates a fresh non-secret random salt (16 bytes, base64) — used for both the account salt and `linkSalt`. */
export function generateSalt(): string {
  return encodeBase64(nobleRandomBytes(16));
}

/**
 * Generates a 6-digit numeric device-link PIN (Decision 12) via rejection
 * sampling over a 32-bit CSPRNG value, to avoid the small modulo bias a
 * naive `% 1_000_000` would introduce.
 */
export function generateDeviceLinkPin(): string {
  const REJECTION_CEILING = Math.floor(0x100000000 / 1_000_000) * 1_000_000;
  let value: number;
  do {
    const bytes = nobleRandomBytes(4);
    value = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(0, false);
  } while (value >= REJECTION_CEILING);
  return String(value % 1_000_000).padStart(6, "0");
}

/** `sha256(pin + linkSalt)`, hex-encoded — sent to `device-link/create`, never the raw PIN. */
export function hashPin(pin: string, linkSalt: string): string {
  return bytesToHex(sha256(decodeUTF8(`${pin}${linkSalt}`)));
}

/**
 * Wraps `chatKey` under an arbitrary 32-byte `wrapKey` (nacl.secretbox).
 * Returned as a single opaque string, `"<nonceBase64>.<ciphertextBase64>"` —
 * base64 never contains `.`, so this is an unambiguous, schema-friendly way
 * to store both in the single TEXT column the handoff's `db.ts` table calls
 * for (`wrapped_chat_key_password` / `wrapped_chat_key_recovery` /
 * `wrapped_chat_key_pin`), without a separate nonce column per row.
 */
export function wrapChatKey(chatKeyBase64: string, wrapKeyBase64: string): string {
  const nonce = nacl.randomBytes(nacl.secretbox.nonceLength);
  const ciphertext = nacl.secretbox(decodeBase64(chatKeyBase64), nonce, decodeBase64(wrapKeyBase64));
  return `${encodeBase64(nonce)}.${encodeBase64(ciphertext)}`;
}

/** Inverse of `wrapChatKey`. Returns `null` if the wrap key is wrong or the blob is malformed. */
export function unwrapChatKey(wrapped: string, wrapKeyBase64: string): string | null {
  const separatorIndex = wrapped.indexOf(".");
  if (separatorIndex === -1) return null;
  const nonceB64 = wrapped.slice(0, separatorIndex);
  const ciphertextB64 = wrapped.slice(separatorIndex + 1);
  if (!nonceB64 || !ciphertextB64) return null;
  try {
    const opened = nacl.secretbox.open(decodeBase64(ciphertextB64), decodeBase64(nonceB64), decodeBase64(wrapKeyBase64));
    return opened ? encodeBase64(opened) : null;
  } catch {
    return null;
  }
}

export interface EncryptedMessage {
  ciphertext: string; // base64
  nonce: string; // base64
}

/** Encrypts a plaintext message body with the shared chatKey (nacl.secretbox). */
export function encryptMessage(plaintext: string, chatKeyBase64: string): EncryptedMessage {
  const nonce = nacl.randomBytes(nacl.secretbox.nonceLength);
  const ciphertext = nacl.secretbox(decodeUTF8(plaintext), nonce, decodeBase64(chatKeyBase64));
  return { ciphertext: encodeBase64(ciphertext), nonce: encodeBase64(nonce) };
}

/** Decrypts a message body with the shared chatKey. Returns null if it fails to decrypt. */
export function decryptMessage(ciphertextBase64: string, nonceBase64: string, chatKeyBase64: string): string | null {
  const opened = nacl.secretbox.open(
    decodeBase64(ciphertextBase64),
    decodeBase64(nonceBase64),
    decodeBase64(chatKeyBase64)
  );
  if (!opened) return null;
  return encodeUTF8(opened);
}

/** Encrypts raw file bytes with the shared chatKey, for attachment uploads. */
export function encryptBytes(bytes: Uint8Array, chatKeyBase64: string): { ciphertext: Uint8Array; nonce: string } {
  const nonce = nacl.randomBytes(nacl.secretbox.nonceLength);
  const ciphertext = nacl.secretbox(bytes, nonce, decodeBase64(chatKeyBase64));
  return { ciphertext, nonce: encodeBase64(nonce) };
}

/** Decrypts raw file bytes with the shared chatKey. Returns null if it fails to decrypt. */
export function decryptBytes(ciphertext: Uint8Array, nonceBase64: string, chatKeyBase64: string): Uint8Array | null {
  return nacl.secretbox.open(ciphertext, decodeBase64(nonceBase64), decodeBase64(chatKeyBase64));
}
